import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useLanguage } from '@/contexts/LanguageContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { toast } from 'sonner';
import { UserPlus, Check, Clock, Building2, User, Mail, Lock, Eye, EyeOff, Sparkles, CheckCircle, RefreshCw } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const RegisterPage = () => {
  const navigate = useNavigate();
  const { register, logout } = useAuth();
  const { language } = useLanguage();

  // This is a public trial/signup entry point (/register, /start, /start-trial).
  // It must never run inside an already-logged-in session — otherwise a new
  // signup attempt leaves the previous account's token untouched, and
  // navigating anywhere afterward keeps showing that old account's real
  // data instead of a fresh trial.
  useEffect(() => {
    logout();
  }, []);

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    confirmPassword: '',
    organizationName: ''
  });
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [registrationComplete, setRegistrationComplete] = useState(false);
  const [resendingEmail, setResendingEmail] = useState(false);
  const [pendingVerification, setPendingVerification] = useState(false);
  const [pendingEmail, setPendingEmail] = useState('');

  const handleResendVerification = async (emailToResend) => {
    const email = emailToResend || formData.email || pendingEmail;
    setResendingEmail(true);
    try {
      await axios.post(`${API}/email-verification/resend`, { email });
      toast.success('Verification email sent! Please check your inbox.');
    } catch (error) {
      toast.error('Failed to resend verification email. Please try again.');
    } finally {
      setResendingEmail(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (formData.password !== formData.confirmPassword) {
      toast.error(language === 'es' ? 'Las contraseñas no coinciden' : 'Passwords do not match');
      return;
    }

    if (formData.password.length < 6) {
      toast.error(language === 'es' ? 'La contraseña debe tener al menos 6 caracteres' : 'Password must be at least 6 characters');
      return;
    }

    setLoading(true);

    try {
      await register(formData.email, formData.password, formData.name, formData.organizationName);
      // Fire the Google Ads "Sign-up" conversion exactly once, right here where a
      // brand-new center registration has actually just succeeded — not on page
      // load, not on choosing the Register tab, not on an existing-account login.
      if (typeof window.gtag === 'function') {
        window.gtag('event', 'conversion', {
          'send_to': process.env.REACT_APP_GOOGLE_ADS_CONVERSION_LABEL,
          'value': 1.0,
          'currency': 'USD'
        });
      }
      // Show verification required screen instead of navigating
      setRegistrationComplete(true);
      toast.success(language === 'es' 
        ? '¡Cuenta creada! Por favor verifica tu correo electrónico.' 
        : 'Account created! Please check your email to verify your account.');
    } catch (error) {
      console.error('Registration error:', error);
      const errorData = error.response?.data?.detail;
      
      // Check if this is an existing unverified account
      if (errorData && typeof errorData === 'object' && errorData.pending_verification) {
        setPendingVerification(true);
        setPendingEmail(errorData.email || formData.email);
        return;
      }
      
      const errorMessage = typeof errorData === 'string' ? errorData : errorData?.message;
      
      // Check for disposable email error
      if (errorMessage && errorMessage.includes('Temporary or disposable')) {
        toast.error(language === 'es' 
          ? 'Las direcciones de correo electrónico temporales no están permitidas. Por favor use un correo válido.'
          : errorMessage);
      } else {
        toast.error(errorMessage || (language === 'es' ? 'Error al registrarse' : 'Registration failed. Please try again.'));
      }
    } finally {
      setLoading(false);
    }
  };

  // Show verification required screen after registration
  if (registrationComplete) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 flex items-center justify-center p-4">
        <Card className="w-full max-w-md shadow-xl border-0">
          <CardHeader className="text-center pb-2">
            <div className="mx-auto w-20 h-20 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center mb-4">
              <Mail className="w-10 h-10 text-white" />
            </div>
            <CardTitle className="text-2xl">Check Your Email</CardTitle>
            <CardDescription>
              We've sent a verification link to <strong>{formData.email}</strong>
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <h4 className="font-semibold text-blue-800 mb-2">What's next?</h4>
              <ol className="text-sm text-blue-700 space-y-2">
                <li className="flex items-start gap-2">
                  <span className="bg-blue-200 text-blue-800 rounded-full w-5 h-5 flex items-center justify-center text-xs flex-shrink-0 mt-0.5">1</span>
                  Open the email we just sent you
                </li>
                <li className="flex items-start gap-2">
                  <span className="bg-blue-200 text-blue-800 rounded-full w-5 h-5 flex items-center justify-center text-xs flex-shrink-0 mt-0.5">2</span>
                  Click the "Verify Email Address" button
                </li>
                <li className="flex items-start gap-2">
                  <span className="bg-blue-200 text-blue-800 rounded-full w-5 h-5 flex items-center justify-center text-xs flex-shrink-0 mt-0.5">3</span>
                  Start your 30-day free trial!
                </li>
              </ol>
            </div>

            <div className="flex items-start gap-2 bg-amber-50 border border-amber-200 rounded-lg p-3">
              <Mail className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
              <p className="text-xs text-amber-800">
                <strong>Don't see it in your inbox?</strong> Please check your <strong>Junk/Spam folder</strong> too —
                verification emails are sometimes filtered there, especially on the first email from us.
              </p>
            </div>

            <div className="text-center text-sm text-gray-500">
              <p>Still didn't receive the email?</p>
              <Button 
                variant="link" 
                className="p-0 h-auto text-blue-600"
                onClick={handleResendVerification}
                disabled={resendingEmail}
              >
                {resendingEmail ? (
                  <>
                    <RefreshCw className="w-4 h-4 mr-1 animate-spin" />
                    Sending...
                  </>
                ) : (
                  'click here to resend'
                )}
              </Button>
            </div>
            
            <div className="pt-4 border-t">
              <Button 
                variant="outline" 
                className="w-full"
                onClick={() => navigate('/login')}
              >
                Go to Login
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Show pending verification screen for existing unverified accounts
  if (pendingVerification) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-blue-100 flex items-center justify-center p-4">
        <Card className="w-full max-w-md shadow-xl border-0">
          <CardHeader className="text-center pb-2">
            <div className="mx-auto w-20 h-20 bg-gradient-to-br from-blue-500 to-blue-700 rounded-full flex items-center justify-center mb-4">
              <Mail className="w-10 h-10 text-white" />
            </div>
            <CardTitle className="text-2xl text-blue-900">Verification Pending</CardTitle>
            <CardDescription>
              An account with <strong>{pendingEmail}</strong> already exists but hasn't been verified yet.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <h4 className="font-semibold text-blue-800 mb-2">What would you like to do?</h4>
              <p className="text-sm text-blue-700 mb-3">
                We can send you a new verification email to complete your registration.
              </p>
              <Button 
                className="w-full bg-blue-600 hover:bg-blue-700 text-white"
                onClick={() => handleResendVerification(pendingEmail)}
                disabled={resendingEmail}
              >
                {resendingEmail ? (
                  <>
                    <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                    Sending...
                  </>
                ) : (
                  <>
                    <Mail className="w-4 h-4 mr-2" />
                    Resend Verification Email
                  </>
                )}
              </Button>
            </div>
            
            <div className="bg-blue-50/50 border border-blue-100 rounded-lg p-4">
              <h4 className="font-semibold text-blue-800 mb-2">Already verified?</h4>
              <p className="text-sm text-blue-600 mb-3">
                If you've already verified your email, you can log in now.
              </p>
              <Button 
                variant="outline" 
                className="w-full border-blue-300 text-blue-700 hover:bg-blue-50"
                onClick={() => navigate('/login')}
              >
                Go to Login
              </Button>
            </div>
            
            <div className="text-center">
              <Button 
                variant="link" 
                className="text-blue-600 text-sm"
                onClick={() => {
                  setPendingVerification(false);
                  setPendingEmail('');
                }}
              >
                ← Try a different email
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  const trialFeatures = language === 'es' 
    ? [
        '30 días de prueba gratis',
        'Hasta 25 niños (plan básico)',
        'Personal ilimitado',
        'Registro diario de entrada/salida',
        'Informes de contacto de emergencia',
        'Soporte bilingüe (Inglés/Español)'
      ]
    : [
        '30-day free trial',
        'Up to 25 children (basic plan)',
        'Unlimited staff accounts',
        'Daily sign-in/out tracking',
        'Emergency contact reports',
        'Bilingual support (English/Spanish)'
      ];

  return (
    <div className="min-h-screen flex relative overflow-hidden">
      {/* Background - Full width gradient that blends both sides */}
      <div className="absolute inset-0 bg-gradient-to-br from-[#2F6DB5] via-[#4DA3D9]/80 to-[#F8FAFC]"></div>
      
      {/* Animated Background Shapes */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-20 left-[10%] w-72 h-72 bg-white/10 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute bottom-20 left-[20%] w-96 h-96 bg-[#10B981]/15 rounded-full blur-3xl animate-pulse" style={{animationDelay: '1s'}}></div>
        <div className="absolute top-1/3 left-[30%] w-64 h-64 bg-[#E83E8C]/10 rounded-full blur-3xl animate-pulse" style={{animationDelay: '2s'}}></div>
        <div className="absolute top-10 right-[20%] w-80 h-80 bg-[#4DA3D9]/20 rounded-full blur-3xl animate-pulse" style={{animationDelay: '0.5s'}}></div>
      </div>
      
      {/* Floating Elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-[15%] left-[8%] w-3 h-3 bg-white/40 rounded-full animate-bounce" style={{animationDuration: '3s'}}></div>
        <div className="absolute top-[35%] left-[25%] w-2 h-2 bg-[#10B981]/60 rounded-full animate-bounce" style={{animationDuration: '2.5s', animationDelay: '0.5s'}}></div>
        <div className="absolute bottom-[30%] left-[15%] w-4 h-4 bg-white/30 rounded-full animate-bounce" style={{animationDuration: '4s', animationDelay: '1s'}}></div>
      </div>

      {/* Left Side - Benefits */}
      <div className="hidden lg:flex lg:w-[40%] relative z-10">
        <div className="flex flex-col justify-center items-center w-full p-12 text-white">
          <div className="max-w-sm">
            <div className="flex items-center gap-3 mb-6">
              <div className="p-3 bg-white/20 backdrop-blur-sm rounded-2xl">
                <Sparkles className="w-8 h-8 text-white" />
              </div>
              <div>
                <h1 className="text-2xl font-bold">ToDays Kidz</h1>
                <p className="text-white/80">Simple Sign-In</p>
              </div>
            </div>
            
            <div className="bg-[#10B981] text-white px-4 py-2 rounded-full inline-flex items-center gap-2 mb-6">
              <Clock className="w-4 h-4" />
              <span className="font-semibold text-sm">
                {language === 'es' ? '¡Prueba gratuita de 30 días!' : '30-Day Free Trial!'}
              </span>
            </div>
            
            <h2 className="text-xl font-semibold mb-4">
              {language === 'es' ? 'Lo que obtienes:' : 'What you get:'}
            </h2>
            
            <ul className="space-y-3">
              {trialFeatures.map((feature, idx) => (
                <li key={idx} className="flex items-center gap-3 bg-white/10 backdrop-blur-sm rounded-xl p-3 transform hover:translate-x-2 transition-transform duration-300">
                  <div className="w-8 h-8 bg-[#10B981] rounded-lg flex items-center justify-center flex-shrink-0">
                    <Check className="w-4 h-4 text-white" />
                  </div>
                  <span className="text-sm">{feature}</span>
                </li>
              ))}
            </ul>
            
            <p className="mt-6 text-sm text-white/70">
              {language === 'es' 
                ? 'No se requiere tarjeta de crédito. Cancele en cualquier momento.'
                : 'No credit card required. Cancel anytime.'}
            </p>
          </div>
        </div>
      </div>

      {/* Right Side - Registration Form */}
      <div className="w-full lg:w-[60%] flex items-center justify-center p-6 relative z-10">
        <div className="w-full max-w-lg">
          {/* Mobile Header */}
          <div className="lg:hidden text-center mb-6">
            <img 
              src="/simple-sign-in-logo.png" 
              alt="Simple Sign In Logo" 
              className="h-20 w-auto object-contain mx-auto mb-2"
            />
            <h2 className="text-white text-xl font-bold">ToDays Kidz Simple Sign-In</h2>
            <div className="bg-[#10B981] text-white px-4 py-2 rounded-full inline-flex items-center gap-2 mt-3">
              <Clock className="w-4 h-4" />
              <span className="font-semibold text-sm">
                {language === 'es' ? '¡Prueba gratuita de 30 días!' : '30-Day Free Trial!'}
              </span>
            </div>
          </div>

          <Card className="w-full bg-white/95 backdrop-blur-xl border-0 shadow-2xl shadow-[#2F6DB5]/20 rounded-3xl overflow-hidden">
            <CardHeader className="text-center pb-2 pt-8 px-8">
              <div className="hidden lg:flex justify-center mb-4">
                <img 
                  src="/simple-sign-in-logo.png" 
                  alt="ToDays Kidz Logo" 
                  className="h-24 w-auto object-contain transform hover:scale-105 transition-transform duration-300"
                />
              </div>
              <CardTitle className="text-2xl font-bold text-[#1E293B]">
                {language === 'es' ? 'Comienza tu Prueba Gratuita' : 'Start Your Free Trial'}
              </CardTitle>
              <CardDescription className="text-[#64748B] mt-1">
                {language === 'es' 
                  ? 'Crea tu cuenta y comienza a administrar registros'
                  : 'Create your account and start managing sign-ins'}
              </CardDescription>
            </CardHeader>
            
            <CardContent className="px-8 pb-8">
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="organizationName" className="text-[#1E293B] font-medium text-sm">
                    {language === 'es' ? 'Nombre del Centro *' : 'Childcare Center Name *'}
                  </Label>
                  <div className="relative group">
                    <Building2 className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[#64748B] group-focus-within:text-[#2F6DB5] transition-colors" />
                    <Input
                      id="organizationName"
                      value={formData.organizationName}
                      onChange={(e) => setFormData({ ...formData, organizationName: e.target.value })}
                      placeholder={language === 'es' ? 'Guardería Estrellitas' : 'Little Stars Daycare'}
                      required
                      className="pl-12 h-12 border-[#E2E8F0] focus:border-[#2F6DB5] focus:ring-2 focus:ring-[#2F6DB5]/20 rounded-xl bg-[#F8FAFC] hover:bg-white transition-colors"
                      data-testid="register-org-name"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="name" className="text-[#1E293B] font-medium text-sm">
                    {language === 'es' ? 'Nombre del Director *' : 'Director Name *'}
                  </Label>
                  <div className="relative group">
                    <User className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[#64748B] group-focus-within:text-[#2F6DB5] transition-colors" />
                    <Input
                      id="name"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      placeholder={language === 'es' ? 'María García' : 'John Smith'}
                      required
                      className="pl-12 h-12 border-[#E2E8F0] focus:border-[#2F6DB5] focus:ring-2 focus:ring-[#2F6DB5]/20 rounded-xl bg-[#F8FAFC] hover:bg-white transition-colors"
                      data-testid="register-name"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="email" className="text-[#1E293B] font-medium text-sm">
                    {language === 'es' ? 'Correo Electrónico *' : 'Email *'}
                  </Label>
                  <div className="relative group">
                    <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[#64748B] group-focus-within:text-[#2F6DB5] transition-colors" />
                    <Input
                      id="email"
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      placeholder="director@daycare.com"
                      required
                      className="pl-12 h-12 border-[#E2E8F0] focus:border-[#2F6DB5] focus:ring-2 focus:ring-[#2F6DB5]/20 rounded-xl bg-[#F8FAFC] hover:bg-white transition-colors"
                      data-testid="register-email"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="password" className="text-[#1E293B] font-medium text-sm">
                    {language === 'es' ? 'Contraseña *' : 'Password *'}
                  </Label>
                  <div className="relative group">
                    <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[#64748B] group-focus-within:text-[#2F6DB5] transition-colors" />
                    <Input
                      id="password"
                      type={showPassword ? "text" : "password"}
                      value={formData.password}
                      onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                      placeholder={language === 'es' ? 'Mínimo 6 caracteres' : 'At least 6 characters'}
                      required
                      className="pl-12 pr-12 h-12 border-[#E2E8F0] focus:border-[#2F6DB5] focus:ring-2 focus:ring-[#2F6DB5]/20 rounded-xl bg-[#F8FAFC] hover:bg-white transition-colors"
                      data-testid="register-password"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-4 top-1/2 -translate-y-1/2 text-[#64748B] hover:text-[#2F6DB5] transition-colors"
                      tabIndex={-1}
                    >
                      {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                    </button>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="confirmPassword" className="text-[#1E293B] font-medium text-sm">
                    {language === 'es' ? 'Confirmar Contraseña *' : 'Confirm Password *'}
                  </Label>
                  <div className="relative group">
                    <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[#64748B] group-focus-within:text-[#2F6DB5] transition-colors" />
                    <Input
                      id="confirmPassword"
                      type={showConfirmPassword ? "text" : "password"}
                      value={formData.confirmPassword}
                      onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
                      placeholder={language === 'es' ? 'Repetir contraseña' : 'Re-enter password'}
                      required
                      className="pl-12 pr-12 h-12 border-[#E2E8F0] focus:border-[#2F6DB5] focus:ring-2 focus:ring-[#2F6DB5]/20 rounded-xl bg-[#F8FAFC] hover:bg-white transition-colors"
                      data-testid="register-confirm-password"
                    />
                    <button
                      type="button"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      className="absolute right-4 top-1/2 -translate-y-1/2 text-[#64748B] hover:text-[#2F6DB5] transition-colors"
                      tabIndex={-1}
                    >
                      {showConfirmPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                    </button>
                  </div>
                </div>

                <Button
                  type="submit"
                  disabled={loading}
                  className="w-full h-12 bg-gradient-to-r from-[#10B981] to-[#059669] hover:opacity-90 text-white font-semibold rounded-xl shadow-lg shadow-[#10B981]/25 transform hover:scale-[1.02] active:scale-[0.98] transition-all duration-200 mt-2"
                  data-testid="register-submit"
                >
                  {loading ? (
                    <div className="flex items-center gap-2">
                      <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                      {language === 'es' ? 'Creando cuenta...' : 'Creating account...'}
                    </div>
                  ) : (
                    <div className="flex items-center gap-2">
                      <UserPlus className="w-5 h-5" />
                      {language === 'es' ? 'Comenzar Prueba Gratuita' : 'Start Free Trial'}
                    </div>
                  )}
                </Button>

                {/* Legal Links */}
                <div className="mt-4 text-center">
                  <p className="text-xs text-[#64748B]">
                    {language === 'es' 
                      ? 'Al crear una cuenta, aceptas nuestros' 
                      : 'By creating an account, you agree to our'}
                  </p>
                  <div className="flex flex-wrap justify-center gap-2 text-xs mt-2">
                    <a 
                      href="https://todayskidzsimplesignin.com/terms-and-conditions" 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="text-[#2F6DB5] hover:text-[#E83E8C] transition-colors"
                    >
                      {language === 'es' ? 'Términos y Condiciones' : 'Terms & Conditions'}
                    </a>
                    <span className="text-[#E2E8F0]">•</span>
                    <a 
                      href="https://todayskidzsimplesignin.com/privacy-policy" 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="text-[#2F6DB5] hover:text-[#E83E8C] transition-colors"
                    >
                      {language === 'es' ? 'Política de Privacidad' : 'Privacy Policy'}
                    </a>
                    <span className="text-[#E2E8F0]">•</span>
                    <a 
                      href="https://todayskidzsimplesignin.com/sms-consent-enrollment" 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="text-[#2F6DB5] hover:text-[#E83E8C] transition-colors"
                    >
                      {language === 'es' ? 'Consentimiento SMS' : 'SMS Consent'}
                    </a>
                  </div>
                </div>
              </form>

              <div className="mt-6 text-center">
                <p className="text-[#64748B]">
                  {language === 'es' ? '¿Ya tienes cuenta?' : 'Already have an account?'}{' '}
                  <Link to="/login" className="font-semibold text-[#2F6DB5] hover:text-[#E83E8C] transition-colors">
                    {language === 'es' ? 'Iniciar sesión' : 'Sign in'}
                  </Link>
                </p>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default RegisterPage;
